<template>
  <!-- 404: Use default layout -->
  <NuxtLayout v-if="error.statusCode === 404" name="default">
    <div class="page w-full">
      <section class="content-section content-section--first">
        <div class="content-section__container container container--page-content">
          <div class="error-404 my-20 lg:my-32">
            <h1 class="text-center mb-5">404</h1>
            <h2 class="mb-10">{{ t('error.404.title') }}</h2>

            <p class="error-404__message">
              {{ t('error.404.description') }}
            </p>

            <div class="error-404__actions">
              <SharedButton :label="t('error.goBack')" variant="primary" @click="handleError" />
              <SharedButton :label="t('error.home')" variant="secondary" to="/" />
            </div>
          </div>
        </div>
      </section>
    </div>
  </NuxtLayout>

  <!-- 500: Static page without layout -->
  <div v-else class="error-500">
    <header class="error-500__header">
      <div class="error-500__header-container container container--page-content">
        <NuxtLink to="/" class="error-500__logo" aria-label="Home" :title="t('header.logo.title')">
          <img src="/imgs/Logo_Herbert_Seidl.avif" alt="Herbert Seidl Logo" class="error-500__logo-image" width="246" height="50" />
        </NuxtLink>
      </div>
    </header>

    <main class="error-500__main">
      <div class="error-500__container">
        <div class="error-500__content">
          <h1 class="error-500__title text-center mb-5">500</h1>
          <h2 class="error-500__subtitle mb-10">{{ t('error.500.title') }}</h2>

          <p class="error-500__message">{{ t('error.500.description') }}</p>

          <div class="error-500__actions">
            <SharedButton :label="t('error.goBack')" variant="primary" @click="handleError" />
            <SharedButton :label="t('error.home')" variant="secondary" to="/" />
          </div>

          <!-- Development error trace -->
          <div v-if="isDev && error.stack" class="error-500__trace">
            <details class="error-500__details">
              <summary class="error-500__summary">Error Details (Development)</summary>
              <pre class="error-500__stack">{{ error.stack }}</pre>
            </details>
          </div>
        </div>
      </div>
    </main>

    <footer class="error-500__footer">
      <FooterUpperFooter :showAIChat="false" />
      <div class="error-500__footer-content container container--page-content">
        <div class="text-small-normal py-2">© 2025 Herbert Seidl. All rights reserved.</div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
  const props = defineProps<{
    error: {
      statusCode: number;
      statusMessage: string;
      stack?: string;
    };
  }>();

  const isDev = process.env.NODE_ENV === 'development';

  const handleError = () => {
    clearError({ redirect: '/' });
  };

  const { t } = useI18n();

  // Meta tags for error pages
  const title = computed(() => (props.error.statusCode === 404 ? t('error.404.title') : t('error.500.title')));

  const description = computed(() => (props.error.statusCode === 404 ? t('error.404.description') : t('error.500.description')));

  // Set page meta
  useHead({
    title: title.value,
    meta: [
      { name: 'description', content: description },
      { name: 'robots', content: 'noindex, nofollow' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:card', content: 'summary' },
    ],
  });
</script>

<style lang="scss" scoped>
  // 404 Error - uses default layout
  .error-404 {
    @apply text-center py-16;

    &__icon {
      @apply mb-6;
    }

    &__svg {
      @apply w-16 h-16 mx-auto text-gray-400;
    }

    &__title {
      @apply text-2xl font-bold text-gray-900 mb-4;
    }

    &__message {
      @apply text-gray-600 mb-8 leading-relaxed;
    }

    &__actions {
      @apply flex flex-col sm:flex-row gap-4 justify-center;
    }
  }

  // 500 Error - static page without layout
  .error-500 {
    @apply min-h-screen flex flex-col bg-gray-50;

    &__header {
      @apply bg-white border-b border-neutral-lightest py-4;
    }

    &__header-container {
      @apply flex items-center;
    }

    &__logo {
      @apply block;
    }

    &__logo-image {
      @apply h-[50px] w-[246px] object-contain object-center;
    }

    &__main {
      @apply flex-1 flex items-center justify-center py-20;
    }

    &__container {
      @apply w-full max-w-2xl mx-auto px-4;
    }

    &__content {
      @apply text-center;
    }

    &__title {
      @apply text-4xl font-bold text-gray-900;
    }

    &__subtitle {
      @apply text-2xl font-bold text-gray-900 mb-10;
    }

    &__message {
      @apply text-gray-600 mb-8 leading-relaxed;
    }

    &__actions {
      @apply flex flex-col sm:flex-row gap-4 justify-center;
    }

    &__trace {
      @apply mt-8 pt-6 border-t border-gray-200;
    }

    &__details {
      @apply text-left;
    }

    &__summary {
      @apply cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 mb-2;
    }

    &__stack {
      @apply text-xs text-gray-600 bg-gray-100 p-4 rounded overflow-x-auto whitespace-pre-wrap;
    }
  }
</style>
